/**
 * RackConfigurationList — EXISTING/NEW table sections (#995 / #1052)
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { groupRacksByStatus } from './rackListUtils';
import { RackTableRow, RackTableHeader } from './RackTableRow';
import { RACK_EMPTY_STATE_TITLE } from '../../constants/rackConfigurationsSubsectionCopy';
import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../theme';
import { DEFAULT_RACK_CONFIGURATION } from '../../types/rack';
import { FormCompactPrimaryButton } from '../forms/FormCompactPrimaryButton';
import { Plus, AlertCircle, Package } from '../icons';
import {
  resolveRecordTableLayout,
  resolveRecordTableReorderActive,
  useRecordTableSectionReorder,
} from '../recordTable';
import { repeatableSectionShowDragColumn } from '../recordTable/repeatableRowChrome';

import type { RackListSection, RackTableLayout } from './rackListUtils';
import type { RackConfiguration } from '../../types/rack';

interface RackConfigurationListProps {
  racks: RackConfiguration[];
  onRackPress: (rack: RackConfiguration) => void;
  onDuplicateRack?: (rack: RackConfiguration) => void;
  onDeleteRack?: (rack: RackConfiguration) => void;
  canDeleteRack?: (rack: RackConfiguration) => boolean;
  onAddRack: () => void;
  /** Web: reorder within EXISTING or NEW section (#1100). */
  onReorderSection?: (
    section: RackListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  isLoading?: boolean;
  error?: Error | null;
  /** Blocks all list interaction (e.g. session not ready). */
  disabled?: boolean;
  /** View-only: row opens rack details; add/duplicate/delete/reorder hidden. */
  readOnly?: boolean;
  /** Browse-by-location: hide the location column (tag is in the chip filter). */
  hideLocationColumn?: boolean;
  /**
   * Devices view: one flat table (no EXISTING/NEW section headers) with STATUS
   * pills in a column.
   */
  statusInColumn?: boolean;
  /** Devices: allow drag while readOnly (no add/menu) — Story 3.7. */
  allowReorder?: boolean;
  /** Devices: no inner ScrollView — Story 3.9. */
  embeddedInParentScroll?: boolean;
  testID?: string;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
}

interface RackSectionProps {
  title: string;
  section: RackListSection;
  racks: RackConfiguration[];
  showPlaceholder?: boolean;
  onRackPress: (rack: RackConfiguration) => void;
  onDuplicateRack?: (rack: RackConfiguration) => void;
  onDeleteRack?: (rack: RackConfiguration) => void;
  canDeleteRack?: (rack: RackConfiguration) => boolean;
  onAddRack: () => void;
  onReorderSection?: (
    section: RackListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  allowReorder?: boolean;
  hideLocationColumn?: boolean;
  statusInColumn?: boolean;
  hideSectionTitle?: boolean;
  /**
   * Flat Devices table only: index where NEW rows begin (= EXISTING count).
   * Confines a drag to its own status block so a NEW ↔ EXISTING move can never
   * land (#999). Omitted for grouped step sections (single status each).
   */
  reorderStatusBoundary?: number;
  testIDPrefix: string;
  tableLayout: RackTableLayout;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  showAddButton?: boolean;
}

const PLACEHOLDER_RACK: RackConfiguration = {
  ...DEFAULT_RACK_CONFIGURATION,
  id: 0,
  requestId: 0,
  rackStatus: 'new',
  rackPlatform: 'Standard',
  displayOrder: 0,
};

const RackSection: React.FC<RackSectionProps> = ({
  title,
  section,
  racks,
  showPlaceholder = false,
  onRackPress,
  onDuplicateRack,
  onDeleteRack,
  canDeleteRack,
  onAddRack,
  onReorderSection,
  disabled,
  readOnly = false,
  allowReorder = false,
  hideLocationColumn = false,
  statusInColumn = false,
  hideSectionTitle = false,
  reorderStatusBoundary,
  testIDPrefix,
  tableLayout,
  resolveLocationTagName,
  showAddButton = true,
}) => {
  const rowCount = racks.length + (showPlaceholder ? 1 : 0);
  const showTableChrome = racks.length > 0 || showPlaceholder;
  const isCompact = tableLayout === 'compact';
  const reorderActive = resolveRecordTableReorderActive({
    readOnly,
    allowReorder,
    onReorderSection,
    itemCount: racks.length,
  });
  const showDragColumn = repeatableSectionShowDragColumn(reorderActive);

  const canReorderTo = useMemo(() => {
    if (reorderStatusBoundary == null) {
      return undefined;
    }
    const boundary = reorderStatusBoundary;
    return (fromIndex: number, toIndex: number) =>
      fromIndex < boundary === toIndex < boundary;
  }, [reorderStatusBoundary]);

  const {
    draggedIndex,
    dragOverIndex,
    registerRowLayout,
    registerRowRemeasure,
    handleDragHandleStart,
    handleDragHandleMove,
    handleDragHandleEnd,
    handleRowDragOver,
    handleRowDrop,
  } = useRecordTableSectionReorder({
    section,
    onReorderSection,
    canReorderTo,
  });

  return (
    <View style={styles.section}>
      {hideSectionTitle ? null : (
        <Text style={styles.sectionTitle}>{title}</Text>
      )}
      {showTableChrome ? (
        <View style={styles.recordTableCard}>
          <RackTableHeader
            reorderEnabled={reorderActive}
            showDragColumn={showDragColumn}
            tableLayout={tableLayout}
            hideLocationColumn={hideLocationColumn}
            showStatusColumn={statusInColumn}
            readOnly={readOnly}
          />
          {racks.map((rack, index) => (
            <RackTableRow
              key={rack.id ?? `${title}-${rack.displayOrder}`}
              rack={rack}
              onPress={onRackPress}
              onDuplicate={onDuplicateRack}
              onDelete={
                onDeleteRack && canDeleteRack?.(rack) ? onDeleteRack : undefined
              }
              disabled={disabled}
              readOnly={readOnly}
              isLastInTable={index === rowCount - 1}
              testID={`${testIDPrefix}-row-${rack.id}`}
              tableLayout={tableLayout}
              hideLocationColumn={hideLocationColumn}
              showStatusColumn={statusInColumn}
              reorderEnabled={reorderActive}
              rowIndex={index}
              isDragging={draggedIndex === index}
              isDragOver={dragOverIndex === index}
              onDragHandleStart={handleDragHandleStart}
              onDragHandleMove={handleDragHandleMove}
              onDragHandleEnd={handleDragHandleEnd}
              onRowDragOver={handleRowDragOver}
              onRowDrop={handleRowDrop}
              onRowLayout={registerRowLayout}
              registerRowRemeasure={registerRowRemeasure}
              resolveLocationTagName={resolveLocationTagName}
            />
          ))}
          {showPlaceholder ? (
            <RackTableRow
              rack={PLACEHOLDER_RACK}
              isPlaceholder
              isLastInTable
              tableLayout={tableLayout}
              testID={`${testIDPrefix}-placeholder-row`}
            />
          ) : null}
        </View>
      ) : null}
      {section === 'new' && showAddButton ? (
        <View
          style={[styles.addButtonRow, isCompact && styles.addButtonRowCompact]}
        >
          <AddRackButton
            onPress={onAddRack}
            disabled={disabled || readOnly}
            testID={`${testIDPrefix}-add-button`}
            variant={isCompact ? 'compact' : 'primary'}
          />
        </View>
      ) : null}
    </View>
  );
};

interface AddRackButtonProps {
  onPress: () => void;
  disabled?: boolean;
  testID: string;
  variant?: 'primary' | 'compact';
  style?: StyleProp<ViewStyle>;
}

const AddRackButton: React.FC<AddRackButtonProps> = ({
  onPress,
  disabled,
  testID,
  variant = 'primary',
  style,
}) => {
  if (variant === 'compact') {
    return (
      <TouchableOpacity
        testID={testID}
        style={[
          styles.addRackCompactButton,
          disabled && styles.addRackCompactButtonDisabled,
          style,
        ]}
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel="Add Rack"
        activeOpacity={disabled ? 1 : 0.88}
      >
        <View style={styles.addRackCompactIconWrap}>
          <Plus size={14} color={colors.primary} strokeWidth={2.5} />
        </View>
        <Text style={styles.addRackCompactLabel}>Add Rack</Text>
      </TouchableOpacity>
    );
  }

  return (
    <FormCompactPrimaryButton
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel="Add Rack"
      icon={<Plus size={14} color={colors.textLight} />}
      style={style}
    >
      Add Rack
    </FormCompactPrimaryButton>
  );
};

interface RackEmptyStateProps {
  onAddRack: () => void;
  disabled?: boolean;
  testID: string;
  addButtonVariant?: 'primary' | 'compact';
}

/** Centered empty state before the first rack is added. */
const RackEmptyState: React.FC<RackEmptyStateProps> = ({
  onAddRack,
  disabled,
  testID,
  addButtonVariant = 'primary',
}) => (
  <View testID={`${testID}-empty`} style={styles.emptyContainer}>
    <View style={styles.emptyContent}>
      <View style={styles.emptyIconWrap} accessibilityElementsHidden>
        <Package size={48} color={colors.textMuted} />
      </View>
      <Text style={styles.emptyTitle}>{RACK_EMPTY_STATE_TITLE}</Text>
      <View style={styles.emptyAddButtonWrap}>
        <AddRackButton
          onPress={onAddRack}
          disabled={disabled}
          testID={`${testID}-empty-add-button`}
          variant={addButtonVariant}
          // Center within the column: the primary button defaults to
          // alignSelf flex-start, which left-aligned it here (#995 bug 5).
          style={styles.emptyAddButton}
        />
      </View>
    </View>
  </View>
);

export const RackConfigurationList: React.FC<RackConfigurationListProps> = ({
  racks,
  onRackPress,
  onDuplicateRack,
  onDeleteRack,
  canDeleteRack,
  onAddRack,
  onReorderSection,
  isLoading = false,
  error = null,
  disabled = false,
  readOnly = false,
  allowReorder = false,
  hideLocationColumn = false,
  statusInColumn = false,
  embeddedInParentScroll = false,
  testID = 'rack-configuration-list',
  resolveLocationTagName,
}) => {
  const { dimensions } = useResponsive();
  const tableLayout: RackTableLayout = resolveRecordTableLayout(
    dimensions.width,
  );
  const isCompactLayout = tableLayout === 'compact';

  const grouped = useMemo(() => groupRacksByStatus(racks), [racks]);
  const hasExisting = grouped.existing.length > 0;
  const hasNew = grouped.new.length > 0;
  /** Web desktop: + Add Rack only — no ghost placeholder row (⋯ menu regression #1101). */
  const showNewPlaceholder =
    !hasNew && !hasExisting && (Platform.OS !== 'web' || isCompactLayout);
  /** Read-only browse (Location view): hide empty NEW when only EXISTING units. */
  const showNewSection = hasNew || showNewPlaceholder || !readOnly;

  if (isLoading) {
    return (
      <View testID={`${testID}-loading`} style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading racks...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View testID={`${testID}-error`} style={styles.errorContainer}>
        <AlertCircle size={24} color={colors.error} />
        <Text style={styles.errorText}>Failed to load racks</Text>
        <Text style={styles.errorSubtext}>
          Please try again or contact support if the problem continues.
        </Text>
      </View>
    );
  }

  if (racks.length === 0) {
    const emptyShell = (
      <RackEmptyState
        onAddRack={onAddRack}
        disabled={disabled || readOnly}
        testID={testID}
        addButtonVariant={isCompactLayout ? 'compact' : 'primary'}
      />
    );

    if (isCompactLayout && !embeddedInParentScroll) {
      return (
        <ScrollView
          testID={testID}
          style={styles.container}
          contentContainerStyle={styles.emptyScrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {emptyShell}
        </ScrollView>
      );
    }

    return (
      <View testID={testID} style={styles.container}>
        {emptyShell}
      </View>
    );
  }

  const renderFlatList = () => {
    if (statusInColumn) {
      const flatRacks = [...grouped.existing, ...grouped.new];
      return (
        <RackSection
          title=""
          section="existing"
          racks={flatRacks}
          onRackPress={onRackPress}
          onDuplicateRack={onDuplicateRack}
          onDeleteRack={onDeleteRack}
          canDeleteRack={canDeleteRack}
          onAddRack={onAddRack}
          onReorderSection={onReorderSection}
          disabled={disabled}
          readOnly={readOnly}
          allowReorder={allowReorder}
          hideLocationColumn={hideLocationColumn}
          statusInColumn
          hideSectionTitle
          reorderStatusBoundary={grouped.existing.length}
          showAddButton={false}
          testIDPrefix={`${testID}-all`}
          tableLayout={tableLayout}
          resolveLocationTagName={resolveLocationTagName}
        />
      );
    }

    return (
      <>
        {hasExisting ? (
          <RackSection
            title="EXISTING"
            section="existing"
            racks={grouped.existing}
            onRackPress={onRackPress}
            onDuplicateRack={onDuplicateRack}
            onDeleteRack={onDeleteRack}
            canDeleteRack={canDeleteRack}
            onAddRack={onAddRack}
            onReorderSection={onReorderSection}
            disabled={disabled}
            readOnly={readOnly}
            allowReorder={allowReorder}
            hideLocationColumn={hideLocationColumn}
            showAddButton={!readOnly}
            testIDPrefix={`${testID}-existing`}
            tableLayout={tableLayout}
            resolveLocationTagName={resolveLocationTagName}
          />
        ) : null}

        {showNewSection ? (
          <RackSection
            title="NEW"
            section="new"
            racks={grouped.new}
            hideSectionTitle={!hasNew}
            showPlaceholder={showNewPlaceholder}
            onRackPress={onRackPress}
            onDuplicateRack={onDuplicateRack}
            onDeleteRack={onDeleteRack}
            canDeleteRack={canDeleteRack}
            onAddRack={onAddRack}
            onReorderSection={onReorderSection}
            disabled={disabled}
            readOnly={readOnly}
            allowReorder={allowReorder}
            hideLocationColumn={hideLocationColumn}
            showAddButton={!readOnly}
            testIDPrefix={`${testID}-new`}
            tableLayout={tableLayout}
            resolveLocationTagName={resolveLocationTagName}
          />
        ) : null}
      </>
    );
  };

  const listBody = renderFlatList();
  if (embeddedInParentScroll) {
    return (
      <View testID={testID} style={styles.container}>
        {listBody}
      </View>
    );
  }

  return (
    <ScrollView
      testID={testID}
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
    >
      {listBody}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: spacing.lg,
  },
  emptyScrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.lg,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textMuted,
    letterSpacing: 0.7,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  recordRows: {
    gap: spacing.sm,
  },
  /** Web: one bordered card — column headers + rows (#995 mockup). */
  recordTableCard: {
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      default: {},
      web: {
        minWidth: 0,
      },
    }),
  },
  loadingContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: spacing.sm,
  },
  errorContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.error,
    marginTop: spacing.sm,
  },
  errorSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    minHeight: Platform.OS === 'web' ? 480 : 320,
  },
  emptyContent: {
    alignItems: 'center',
    gap: spacing.sm,
    // Definite width so the full-width "Add rack" button resolves predictably and
    // stays centered with the icon/title above it (#995 bug 5).
    width: '100%',
    maxWidth: 360,
  },
  emptyIconWrap: {
    marginBottom: spacing.xs,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyAddButtonWrap: {
    marginTop: spacing.md,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  emptyAddButton: {
    alignSelf: 'center',
  },
  addButtonRow: {
    width: '100%',
    marginTop: spacing.md,
  },
  addButtonRowCompact: {
    marginTop: spacing.sm,
  },
  addRackCompactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    width: '100%',
    minHeight: 48,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.textMuted,
    backgroundColor: colors.backgroundPrimary,
  },
  addRackCompactButtonDisabled: {
    opacity: 0.5,
  },
  addRackCompactIconWrap: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
  },
  addRackCompactLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
  },
});
